/**
 * Phase 126.K — Classroom Support plans library section.
 *
 * Renders the teacher's saved Hub support plans (ADHD / Gifted /
 * Autism) on the Library tab. Plans auto-save server-side on
 * generation (see `routes/classroom_support.py::support_generate`)
 * so there's no save action on the generator page itself.
 *
 * Per-row actions: Open (deep-link back into the generator with the
 * saved plan re-rendered), Export PDF (branded), Delete.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Brain,
  Download,
  Link2,
  Link2Off,
  Loader2,
  Puzzle,
  Sparkles,
  Trash2,
  Wrench,
} from "lucide-react";
import ConfirmDeleteDialog from "../ConfirmDeleteDialog";
import LibraryRecentStrip from "../LibraryRecentStrip";
import CompanionToolsRow from "./CompanionToolsRow";

const apiBase = () => `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const PROFILE_ICON = {
  adhd: Brain,
  gifted: Sparkles,
  autism: Puzzle,
};

const PROFILE_ACCENT = {
  adhd: "from-[#96ded1]/40 to-[#96ded1]/10",
  gifted: "from-[#f6c177]/40 to-[#f6c177]/10",
  autism: "from-[#7aa2f7]/40 to-[#7aa2f7]/10",
};

const API_PROFILE_TO_URL = {
  adhd: "adhd",
  gifted: "giftedness",
  autism: "autism",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

export default function ClassroomSupportPlansSection() {
  const [plans, setPlans] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // Phase 130 — Reliable in-app delete confirmation (replaces
  // window.confirm() which is silently blocked on some mobile WebViews).
  const [pendingDelete, setPendingDelete] = useState(null);
  const [linkingId, setLinkingId] = useState(null); // plan id mid-link
  const [pickerOpenFor, setPickerOpenFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [planRes, unitRes] = await Promise.all([
        fetch(`${apiBase()}/classroom-support/plans`, { credentials: "include" }),
        fetch(`${apiBase()}/activities?limit=500`, { credentials: "include" }),
      ]);
      if (!planRes.ok) {
        if (planRes.status === 401) {
          setPlans([]);
          return;
        }
        throw new Error(`HTTP ${planRes.status}`);
      }
      const data = await planRes.json();
      setPlans(data.plans || []);
      if (unitRes.ok) {
        const u = await unitRes.json();
        // /api/activities can return either an array or {activities: [...]}
        const list = Array.isArray(u) ? u : u.activities || [];
        setUnits(
          list
            .filter((a) => a.output_type === "unit")
            .map((a) => ({ id: a.id, name: a.activity_name || "Untitled unit" }))
        );
      }
    } catch (e) {
      setError(e.message || "Couldn't load saved plans");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleExport(planId, profile) {
    setExportingId(planId);
    try {
      const res = await fetch(
        `${apiBase()}/classroom-support/plans/${planId}/export`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ branded: true }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${profile}-support-plan.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Couldn't export the plan");
    } finally {
      setExportingId(null);
    }
  }

  async function handleDelete(planId) {
    // Phase 130 — Open in-app dialog instead of window.confirm.
    const plan = plans.find((p) => p.id === planId);
    setPendingDelete(plan || { id: planId });
  }

  async function performDelete(planId) {
    setDeletingId(planId);
    try {
      const res = await fetch(
        `${apiBase()}/classroom-support/plans/${planId}`,
        { method: "DELETE", credentials: "include" },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setPlans((prev) => prev.filter((p) => p.id !== planId));
      toast.success("Plan deleted.");
    } catch (e) {
      toast.error(e.message || "Couldn't delete the plan");
    } finally {
      setDeletingId(null);
    }
  }

  async function handleLink(planId, unitId) {
    setLinkingId(planId);
    try {
      const res = await fetch(
        `${apiBase()}/classroom-support/plans/${planId}/link`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ unit_id: unitId }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      setPlans((prev) => prev.map((p) => (p.id === planId ? updated : p)));
      setPickerOpenFor(null);
      if (unitId) {
        toast.success(`Linked to "${updated.linked_unit_name}".`);
      } else {
        toast.success("Unlinked from unit.");
      }
    } catch (e) {
      toast.error(e.message || "Couldn't update the link");
    } finally {
      setLinkingId(null);
    }
  }

  const hasUnits = useMemo(() => units.length > 0, [units]);

  if (loading) {
    return (
      <div
        className="ae-card p-5"
        data-testid="classroom-support-plans-loading"
      >
        <p className="text-sm text-[#4a6984]/80 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading Classroom Support plans…
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="ae-card p-5"
        data-testid="classroom-support-plans-error"
      >
        <p className="text-sm text-rose-700">{error}</p>
      </div>
    );
  }

  if (!plans.length) {
    // Hide the section entirely when there's nothing — keeps the
    // library tidy for teachers who don't use the Hub.
    return null;
  }

  return (
    <section
      className="ae-card p-5 sm:p-6"
      data-testid="classroom-support-plans-section"
    >
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70 mb-1">
            Classroom Support plans
          </p>
          <h3 className="font-extrabold text-lg text-[#1c2d3d]">
            {plans.length} saved plan{plans.length === 1 ? "" : "s"}
          </h3>
        </div>
        <Link
          to="/classroom-support"
          data-testid="classroom-support-plans-hub-link"
          className="text-sm font-bold text-[#4a6984] hover:text-[#1c2d3d] underline decoration-[#96ded1] decoration-2 underline-offset-2"
        >
          + Generate a new plan
        </Link>
      </div>

      {(() => {
        const recentPlans = [...plans]
          .sort((a, b) => {
            const ta = a?.created_at ? Date.parse(a.created_at) : 0;
            const tb = b?.created_at ? Date.parse(b.created_at) : 0;
            return tb - ta;
          })
          .slice(0, 3);
        return (
          <LibraryRecentStrip
            testid="classroom-support-recent-strip"
            items={recentPlans.map((p) => ({
              id: p.id,
              title: p.label || "Support plan",
              subtitle: [
                p.grade ? `Grade ${p.grade}` : "",
                p.subject || "",
              ]
                .filter(Boolean)
                .join(" · "),
              dateLabel: formatDate(p.created_at),
              href: `/classroom-support/${
                API_PROFILE_TO_URL[p.profile] || p.profile
              }/generator?plan=${encodeURIComponent(p.id)}`,
            }))}
          />
        );
      })()}

      <ul className="space-y-2.5">
        {plans.map((p) => {
          const Icon = PROFILE_ICON[p.profile] || Wrench;
          const accent = PROFILE_ACCENT[p.profile] || "from-[#96ded1]/30 to-[#96ded1]/10";
          const refSlug = API_PROFILE_TO_URL[p.profile] || p.profile;
          return (
            <li
              key={p.id}
              data-testid={`classroom-support-plan-row-${p.id}`}
              className="border border-[#4a6984]/15 rounded-lg p-3.5 hover:border-[#96ded1]/60 transition-colors flex items-start gap-3"
            >
              <span
                className={`inline-flex h-10 w-10 rounded-lg bg-gradient-to-br ${accent} grid place-items-center shrink-0 mt-0.5`}
              >
                <Icon className="h-4.5 w-4.5 text-[#1c2d3d]" strokeWidth={2} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#1c2d3d] px-2 py-0.5 rounded-full bg-[#96ded1]/25"
                    data-testid={`classroom-support-plan-label-${p.id}`}
                  >
                    {p.label}
                  </span>
                  {p.grade ? (
                    <span className="text-[11px] font-bold text-[#4a6984]/80">
                      Grade {p.grade}
                    </span>
                  ) : null}
                  {p.subject ? (
                    <span className="text-[11px] font-bold text-[#4a6984]/80">
                      · {p.subject}
                    </span>
                  ) : null}
                  <span className="text-[11px] text-[#4a6984]/70 ml-auto">
                    {formatDate(p.created_at)}
                  </span>
                </div>
                <p className="mt-1.5 text-sm text-[#4a6984] leading-snug break-words">
                  {p.situation_excerpt || "(no situation recorded)"}
                </p>
                {/* Link state row — Phase 126.L. When linked, show a
                    pill with the unit name + unlink. When unlinked,
                    expose the "Attach to unit" picker.  */}
                {p.linked_unit_id ? (
                  <div
                    className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-[#1c2d3d]"
                    data-testid={`classroom-support-plan-linked-${p.id}`}
                  >
                    <Link2 className="h-3.5 w-3.5 text-[#4a6984]" />
                    <span className="px-2 py-0.5 rounded-full bg-[#96ded1]/25 border border-[#96ded1]/60">
                      Linked to {p.linked_unit_name || "this unit"}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleLink(p.id, null)}
                      disabled={linkingId === p.id}
                      data-testid={`classroom-support-plan-unlink-${p.id}`}
                      className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-rose-700 hover:text-rose-800 font-extrabold transition disabled:opacity-50"
                    >
                      {linkingId === p.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Link2Off className="h-3 w-3" />
                      )}
                      Unlink
                    </button>
                  </div>
                ) : pickerOpenFor === p.id && hasUnits ? (
                  <div
                    className="mt-2 flex items-center gap-2 flex-wrap"
                    data-testid={`classroom-support-plan-link-picker-${p.id}`}
                  >
                    <select
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) handleLink(p.id, e.target.value);
                      }}
                      disabled={linkingId === p.id}
                      data-testid={`classroom-support-plan-link-select-${p.id}`}
                      className="text-xs font-bold border border-[#4a6984]/25 rounded-md px-2 py-1.5 bg-white text-[#1c2d3d] focus:outline-none focus:border-[#96ded1] max-w-[280px]"
                    >
                      <option value="" disabled>
                        Pick a unit to attach to…
                      </option>
                      {units.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setPickerOpenFor(null)}
                      className="text-[10px] uppercase tracking-[0.18em] text-[#4a6984] hover:text-[#1c2d3d] font-extrabold"
                    >
                      Cancel
                    </button>
                  </div>
                ) : null}
                <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                  <Link
                    to={`/classroom-support/${refSlug}/generator?plan=${encodeURIComponent(p.id)}`}
                    data-testid={`classroom-support-plan-open-${p.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#96ded1] hover:bg-[#7fd0c1] text-[#1c2d3d] text-xs font-extrabold transition"
                  >
                    Open
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleExport(p.id, p.profile)}
                    disabled={exportingId === p.id}
                    data-testid={`classroom-support-plan-export-${p.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#1c2d3d] text-xs font-bold border border-[#4a6984]/25 hover:bg-[#96ded1]/15 transition disabled:opacity-50"
                  >
                    {exportingId === p.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    PDF
                  </button>
                  {!p.linked_unit_id && hasUnits && pickerOpenFor !== p.id ? (
                    <button
                      type="button"
                      onClick={() => setPickerOpenFor(p.id)}
                      data-testid={`classroom-support-plan-link-open-${p.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#1c2d3d] text-xs font-bold border border-[#4a6984]/25 hover:bg-[#96ded1]/15 transition"
                    >
                      <Link2 className="h-3.5 w-3.5" />
                      Attach to unit
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => handleDelete(p.id)}
                    disabled={deletingId === p.id}
                    data-testid={`classroom-support-plan-delete-${p.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-rose-700 text-xs font-bold border border-rose-200 hover:bg-rose-50 transition disabled:opacity-50"
                  >
                    {deletingId === p.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                    Delete
                  </button>
                </div>
                <CompanionToolsRow
                  planId={p.id}
                  variant="inline"
                  testidPrefix={`classroom-support-plan-companion-${p.id}`}
                />
              </div>
            </li>
          );
        })}
      </ul>
      {/* Phase 130 — Reliable in-app delete confirmation. */}
      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title={`Delete "${pendingDelete?.title || "this plan"}"?`}
        description="This permanently removes the saved support plan from your library. This cannot be undone."
        confirmLabel="Delete plan"
        testidPrefix={`classroom-support-plan-delete-confirm-${pendingDelete?.id || "unknown"}`}
        onConfirm={async () => {
          if (pendingDelete?.id) {
            await performDelete(pendingDelete.id);
          }
        }}
      />
    </section>
  );
}
